import { useState, useEffect } from 'react';
import { isLoggedIn, getFirstName } from '../utils/userUtils';
import { useHydrated } from '../hooks/useHydrated';
import { resolvedComponents } from '../hydrationPreload';
import { PageTitle } from './common';

/**
 * Single owner of the brand title. `titleVariant` picks how it renders:
 * - "logo" (default): small clickable logo in the nav row - content pages
 *   (/terms, /about, admin, the Phase 1 inert SSR search pages) where it's the
 *   only branding and the way back home.
 * - "hero": the big centered homepage-style title below the nav row - pages whose
 *   content is the Hero search experience (HomePage, the hydrated /search/{slug}
 *   tree). Owned here rather than by Hero so no page can end up with both titles
 *   stacked (the old bug: Hero drew its own title while Header path-sniffed
 *   location to decide whether to add the logo).
 */
function Header({ titleVariant = 'logo' }) {
  const hydrated = useHydrated();
  // Resolved by preloadHydratedChunks() before `hydrated` can ever be true - see
  // hydrationPreload.js for why these are no longer lazy()+Suspense.
  const UserMenu = resolvedComponents.UserMenu;
  const AuthModal = resolvedComponents.AuthModal;
  // openAuthModal events are counted here, in the always-mounted Header, because
  // AuthModal doesn't mount until hydration completes and its chunk is resolved - a
  // listener inside the modal would miss any event fired before that.
  const [authModalOpenRequests, setAuthModalOpenRequests] = useState(0);

  useEffect(() => {
    const handleOpenAuthModal = () => setAuthModalOpenRequests((count) => count + 1);
    document.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => document.removeEventListener('openAuthModal', handleOpenAuthModal);
  }, []);

  return (
    <header className="pb-4 pt-2" role="banner">
      <nav className="flex justify-between items-center" aria-label="Main Navigation">
        <div className='user-menu flex items-center gap-3'>
          {/* Gated on `hydrated`, not just called directly - isLoggedIn() reads
              document.cookie, which the SSR stub (src/ssr/stubs/userUtils.js) always
              answers as logged-out. A logged-in visitor's first client render would
              otherwise jump straight to UserMenu, mismatching the server-rendered
              logged-out button (React #418/#423) - see useHydrated.js. */}
          {hydrated && isLoggedIn() ? (
            UserMenu ? <UserMenu /> : <div className="text-center py-4">שלום {getFirstName()}</div>
          ) : (
            <>
              <button
                className="log-in-button px-4 py-1 text-sm font-semibold text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                onClick={() => document.dispatchEvent(new CustomEvent('openAuthModal'))}
                aria-haspopup="dialog"
              >
                התחברו
              </button>
            </>
          )}
        </div>
        {titleVariant === 'logo' && (
          /* Real anchor (not onClick) so the logo also works on the inert SSR search
             pages, where no client-side JS runs. Same full-page navigation as before. */
          <a href="/" aria-label="בוא תימצא">
            <PageTitle variant="logo" level="h3" id="logo" center showBeta>
              בואתִמצא
            </PageTitle>
          </a>
        )}
        {/* <div className="space-x-4">
          <a 
            href="#" 
            className="text-gray-600 hover:text-indigo-600"
            aria-label="אודות"
          >
            אודות
          </a>
        </div> */}
      </nav>

      {titleVariant === 'hero' && (
        <PageTitle variant="hero" center showBeta ariaLabel="בוא תימצא">
          בואתִמצא
        </PageTitle>
      )}

      {/* Mounted only once the user actually asks for it (clicks "התחברו"), not the
          instant hydration completes - a search page can have dozens of other
          hydration-gated components (one ItemFullscreenModal per product card, plus
          InstructionsModal, TrackItemModal, banners) all trying to mount in the very
          same commit, which is what corrupted React's hooks dispatcher (#321/#418/#423
          cascade, docs/seo-ssr-search-pages-design.md). AuthModal's chunk is still
          preloaded eagerly (hydrationPreload.js) so there's no load delay on first
          click - only the *mount* is deferred, not the fetch. */}
      {authModalOpenRequests > 0 && AuthModal && (
        <AuthModal
          openRequests={authModalOpenRequests}
          onAuthSuccess={() => {
            // Refresh the page after successful login
            window.location.reload();
          }}
        />
      )}
    </header>
  );
}

export default Header;
